import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import { useLang } from "@/lib/lang";

export default function PrivacyPage() {
  const { t, lang } = useLang();
  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="privacy-page">
      <Header />
      <div className="pt-24 pb-16 max-w-4xl mx-auto px-6">
        <p className="label-mono mb-4">{t("terms_label")}</p>
        <h1 className="font-['Unbounded'] text-4xl font-black text-white mb-8">{t("privacy_heading")}</h1>
        <div className="space-y-6 text-[#A0A0AB] text-sm leading-relaxed">
          <p className="text-white text-base">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">1. Introduction</h2>
            <p>Fortis Digital LLC ("Company", "we", "us") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website fortisdigitalllc.com and use our marketplace platform ("Service").</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">2. Information We Collect</h2>
            <p className="font-semibold text-white mt-3 mb-2">Personal Information:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name, email address, and contact information when you create an account</li>
              <li>Payment information (processed securely through Stripe — we do not store card details)</li>
              <li>Business information for creator accounts (company name, bio)</li>
              <li>Tax identification information for non-profit applications</li>
              <li>Messages submitted through our contact form</li>
            </ul>
            <p className="font-semibold text-white mt-3 mb-2">Automatically Collected Information:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>IP address, browser type, and operating system</li>
              <li>Pages visited, time spent on pages, and navigation patterns</li>
              <li>Device information and screen resolution</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide, operate, and maintain our Platform</li>
              <li>To process transactions and send related information (receipts, confirmations)</li>
              <li>To manage your account and provide customer support</li>
              <li>To send administrative notifications (account updates, security alerts)</li>
              <li>To process creator payouts and manage subscription tiers</li>
              <li>To verify non-profit organization status</li>
              <li>To improve our Platform and develop new features</li>
              <li>To prevent fraud and ensure Platform security</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">4. Information Sharing</h2>
            <p>We do not sell your personal information. We may share information with:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><span className="text-white">Payment processors:</span> Stripe, for processing payments</li>
              <li><span className="text-white">Email service providers:</span> Resend, for transactional emails</li>
              <li><span className="text-white">Law enforcement:</span> When required by law or to protect our rights</li>
              <li><span className="text-white">Business transfers:</span> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">5. Data Security</h2>
            <p>We implement industry-standard security measures including encryption, secure authentication (JWT tokens with bcrypt password hashing), and secure payment processing through Stripe. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">6. Data Retention</h2>
            <p>We retain your personal information for as long as your account is active or as needed to provide services. We may retain certain information as required by law or for legitimate business purposes such as fraud prevention and record keeping.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">7. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Access and receive a copy of your personal data</li>
              <li>Correct inaccurate or incomplete data</li>
              <li>Request deletion of your personal data</li>
              <li>Object to processing of your data</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="mt-2">To exercise these rights, contact us at <span className="text-[#00F0FF]">info@fortisdigitalllc.com</span>.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">8. Children's Privacy</h2>
            <p>Our Platform is not directed to individuals under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected information from a child, we will take steps to delete it.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">9. Cookies</h2>
            <p>We use local storage and session storage for authentication purposes (JWT tokens). We may use analytics cookies to understand how users interact with our Platform. You can control cookie preferences through your browser settings.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by posting a notice on our Platform. Your continued use of the Platform after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">11. Contact Us</h2>
            <p>For privacy-related questions or concerns:</p>
            <div className="glass-card rounded-2xl p-5 mt-3">
              <p className="text-white font-semibold">Fortis Digital LLC</p>
              <p className="text-[#00F0FF]">info@fortisdigitalllc.com</p>
              <p>fortisdigitalllc.com</p>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
